// =============================================================================
// Browser side of the audience measurement: tiny beacons to /api/track,
// sent without blocking navigation (sendBeacon, fetch keepalive otherwise).
// =============================================================================

export type ClientEventName = "PROJECT_LINK" | "NETWORK_CLICK";

type Beacon =
  | { type: "pageview"; path: string; referrer?: string | null }
  | {
      type: "event";
      name: ClientEventName;
      path: string;
      meta?: Record<string, string>;
    };

function send(beacon: Beacon) {
  try {
    const body = JSON.stringify(beacon);
    const blob = new Blob([body], { type: "application/json" });
    if (navigator.sendBeacon?.("/api/track", blob)) return;
    void fetch("/api/track", {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Measurement never breaks the page
  }
}

export function trackPageView(path: string, referrer?: string | null) {
  send({ type: "pageview", path, referrer });
}

/**
 * Data attributes of a tracked link: a click on it sends the event (see the
 * delegated listener of PageViewTracker), even from a server component.
 * @example <a href={url} {...trackAttributes("PROJECT_LINK", { project: slug })}>
 */
export function trackAttributes(
  name: ClientEventName,
  meta: Record<string, string> = {}
): Record<string, string> {
  return {
    "data-track": name,
    ...Object.fromEntries(
      Object.entries(meta).map(([key, value]) => [`data-track-${key}`, value])
    ),
  };
}

/** Event of a clicked link carrying trackAttributes */
export function eventOfLink(
  link: HTMLElement
): { name: ClientEventName; meta: Record<string, string> } | null {
  const name = link.dataset.track;
  if (name !== "PROJECT_LINK" && name !== "NETWORK_CLICK") return null;
  const meta = Object.fromEntries(
    Object.entries(link.dataset)
      .filter(([key]) => key.startsWith("track") && key !== "track")
      .map(([key, value]) => [
        key.charAt(5).toLowerCase() + key.slice(6),
        value ?? "",
      ])
  );
  return { name, meta };
}

/** Click on a project link, a network… */
export function trackEvent(
  name: ClientEventName,
  meta?: Record<string, string>
) {
  send({ type: "event", name, path: window.location.pathname, meta });
}

// =============================================================================
